import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Sortable from 'sortablejs';
import { Tag, Tooltip, Input, Button, Empty, Spin, Modal, message } from 'antd';
import { Edit2, Trash2, Image as ImageIcon, Plus, X } from 'react-feather';
import ToggleSwitch from '../../../CommonComponents/ToggleSwitch/ToggleSwitch';
import ClipPath from '../../../../assets/icons/ic_clip-path.svg';
import createPrivateAdminPanelInstance from '../../../../api/PrivateAdminPanelInstance';

const ENTITY_TYPE = 'ALL_SPORTS';

// Main Shortcuts component with all logic
const Shortcuts = () => {
  const token = JSON.parse(localStorage.getItem('token'));
  const PrivateAdminPanelInstance = useMemo(
    () => createPrivateAdminPanelInstance(token?.IdToken),
    [token?.IdToken]
  );

  const [loading, setLoading] = useState(false);
  const [rowBusy, setRowBusy] = useState({});
  const [reordering, setReordering] = useState(false);
  const [renameSaving, setRenameSaving] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);

  // Fetch shortcuts data
  const fetchShortcuts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await PrivateAdminPanelInstance.post('get-shortcuts-ordering', {
        entity_type: ENTITY_TYPE,
        include_deleted: false,
        include_inactive: true,
      });
      const shortcuts = data?.data?.shortcuts || [];
      setItems(shortcuts);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load shortcuts');
    } finally {
      setLoading(false);
    }
  }, [PrivateAdminPanelInstance]);

  // Initial load
  useEffect(() => {
    fetchShortcuts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handler functions
  const handleActiveToggle = async (record) => {
    const id = record.entity_id;
    if (rowBusy[id]) return;
    setRowBusy((m) => ({ ...m, [id]: true }));
    const prev = record.is_active;
    // optimistic update
    setItems((prevItems) => prevItems.map((it) => it.entity_id === id ? { ...it, is_active: !prev } : it));
    try {
      await PrivateAdminPanelInstance.post('set-shortcut-activity-status', {
        entity_id: id,
        is_active: !prev,
      });
    } catch (e) {
      // revert on error
      setItems((prevItems) => prevItems.map((it) => it.entity_id === id ? { ...it, is_active: prev } : it));
      setError(e?.response?.data?.message || 'Failed to update status');
    } finally {
      setRowBusy((m) => ({ ...m, [id]: false }));
    }
  };

  const handleDelete = async () => {
    const id = showDeleteModal;
    if (!id) return;
    setRowBusy((m) => ({ ...m, [id]: true }));
    try {
      await PrivateAdminPanelInstance.post('delete-shortcut', { entity_id: id });
      setItems((prevItems) => prevItems.filter((it) => it.entity_id !== id));
      message.success('Shortcut deleted');
      setShowDeleteModal(null);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to delete shortcut');
    } finally {
      setRowBusy((m) => ({ ...m, [id]: false }));
    }
  };

  const handleRename = async (name) => {
    if (!renameTarget) return;
    const id = renameTarget.entity_id;
    setRenameSaving(true);
    try {
      await PrivateAdminPanelInstance.post('rename-shortcut', {
        entity_id: id,
        updated_name: name,
      });
      setItems((prevItems) => prevItems.map((it) => it.entity_id === id ? { ...it, display_name: name } : it));
      setRenameTarget(null);
      message.success('Shortcut renamed');
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to rename');
    } finally {
      setRenameSaving(false);
    }
  };

  const handleReorder = async (orderedIds) => {
    const prevItems = items;
    const mapById = new Map(prevItems.map((it) => [String(it.entity_id), it]));
    const reordered = orderedIds.map((id) => mapById.get(String(id))).filter(Boolean);
    setItems(reordered);
    try {
      setReordering(true);
      await PrivateAdminPanelInstance.post('reorder-shortcuts', { ordered_ids: orderedIds });
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to reorder');
      setItems(prevItems);
    } finally {
      setReordering(false);
    }
  };

  return (
    <>
      <div className="head-controls">
        <div className="msb-actions">
          <Button type="primary" className="add-btn" onClick={() => setShowAddModal(true)}>
            <Plus size={16} />
            <span>Add Shortcut</span>
          </Button>
        </div>
      </div>

      {error ? <div className="error-msg">{error}</div> : null}

      {loading ? (
        <div className="center"><Spin /></div>
      ) : (
        <div className="msb-panel">
          <ShortcutsList
            items={items}
            onRename={(row) => setRenameTarget(row)}
            onToggleActive={handleActiveToggle}
            onDelete={(id) => setShowDeleteModal(id)}
            busyIds={rowBusy}
            onReorder={handleReorder}
          />
          {!loading && items.length === 0 && (
            <div className="empty-container"><Empty description="No shortcuts" /></div>
          )}
        </div>
      )}

      <Modal closable={false} open={showAddModal} onCancel={() => setShowAddModal(false)} footer={null} width={600}>
        <AddShortcutModal
          onClose={() => setShowAddModal(false)}
          onCreated={async () => { setShowAddModal(false); await fetchShortcuts(); }}
          PrivateAdminPanelInstance={PrivateAdminPanelInstance}
        />
      </Modal>

      <Modal closable={false} open={!!showDeleteModal} onCancel={() => setShowDeleteModal(null)} footer={null} width={500}>
        <DeleteShortcutModal onClose={() => setShowDeleteModal(null)} onConfirm={handleDelete} busy={!!rowBusy[showDeleteModal || '']} />
      </Modal>

      <Modal closable={false} open={!!renameTarget} onCancel={() => setRenameTarget(null)} footer={null} width={500}>
        {renameTarget ? (
          <RenameShortcutModal
            key={renameTarget?.entity_id}
            target={renameTarget}
            onClose={() => setRenameTarget(null)}
            onConfirm={handleRename}
            saving={renameSaving}
          />
        ) : null}
      </Modal>
    </>
  );
};

// Pure layout component for Shortcuts rows
const ShortcutsList = ({
  items,
  onRename,
  onToggleActive,
  onDelete,
  onReorder,
  busyIds = {},
}) => {
  const listRef = useRef(null);
  const sortableRef = useRef(null);

  useEffect(() => {
    if (!listRef.current) return;
    if (sortableRef.current) {
      try { sortableRef.current.destroy(); } catch (_) {}
      sortableRef.current = null;
    }
    sortableRef.current = Sortable.create(listRef.current, {
      animation: 150,
      handle: '.drag-handle',
      draggable: '.shortcut-row',
      onEnd: (evt) => {
        if (evt.oldIndex === evt.newIndex) return;
        const nodes = Array.from(listRef.current.querySelectorAll('.shortcut-row'));
        const orderedIds = nodes.map((n) => n.getAttribute('data-id'));
        onReorder?.(orderedIds);
      },
    });
    return () => {
      try { sortableRef.current?.destroy(); } catch (_) {}
      sortableRef.current = null;
    };
  }, [items, onReorder]);

  return (
    <div className="shortcuts-table">
      <div className="shortcuts-header">
        <div className="h-order"># Order</div>
        <div className="h-icon">Icon</div>
        <div className="h-name">Displayed Name</div>
        <div className="h-item">Item</div>
        <div className="h-status">Status</div>
        <div className="h-action">Action</div>
      </div>
      <div className="shortcuts-body" ref={listRef}>
        {items?.map((row, idx) => (
          <div className="shortcut-row" key={row.entity_id} data-id={row.entity_id}>
            <div className="order-cell">
              <img className="drag-handle" src={ClipPath} alt="Drag" />
              <div className="order-pill">{idx + 1}</div>
            </div>
            <div className="icon-cell">
              {row.image_link ? (
                <img
                  className="shortcut-icon"
                  src={row.image_link}
                  alt={`${row.display_name} icon`}
                />
              ) : (
                <div className="shortcut-icon placeholder" title="No icon">
                  <ImageIcon size={18} />
                </div>
              )}
            </div>
            <div className="name-cell">
              <span className="name">{row.display_name}</span>
              <button className="icon-btn ghost" title="Rename" onClick={() => onRename(row)} disabled={!!busyIds[row.entity_id]}>
                <Edit2 size={14} />
              </button>
            </div>
            <div className="item-cell">
              <span className="item-name">{row.target_name}</span>
              <Tag color={row.target_type === 'LEAGUE' ? 'blue' : row.target_type === 'SPORT' ? 'green' : 'gold'}>
                {row.target_type}
              </Tag>
              {row.target_type === 'LEAGUE' && (row.country_tag || row.country_name) ? (
                <Tag color="purple">{row.country_tag || row.country_name}</Tag>
              ) : null}
            </div>
            <div className="status-cell">
              <ToggleSwitch status={row.is_active} onlyUpdate={true} data={row} updateEventCode={onToggleActive} disabled={!!busyIds[row.entity_id]} />
            </div>
            <div className="action-cell">
              <Tooltip title="Delete">
                <button className="icon-btn danger" onClick={() => onDelete(row.entity_id)} disabled={!!busyIds[row.entity_id]}>
                  <Trash2 size={16} />
                </button>
              </Tooltip>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Modal components
const AddShortcutModal = ({ onClose, onCreated, PrivateAdminPanelInstance }) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');

  const fetchOptions = useMemo(() => {
    let timer;
    return async (q) => {
      if (timer) clearTimeout(timer);
      return new Promise((resolve) => {
        timer = setTimeout(async () => {
          if (!q) {
            setOptions([]);
            return resolve();
          }
          setLoading(true);
          setError('');
          try {
            const { data } = await PrivateAdminPanelInstance.post('get-potential-shortcuts-autocomplete', {
              search_term: q,
              search_limit: 10,
              entity_type: ENTITY_TYPE,
              item_types: ['SPORT', 'LEAGUE'],
            });
            setOptions(data?.data || []);
          } catch (e) {
            setError(e?.response?.data?.message || 'Failed to search');
          } finally {
            setLoading(false);
            resolve();
          }
        }, 250);
      });
    };
  }, [PrivateAdminPanelInstance]);

  useEffect(() => {
    fetchOptions(query);
  }, [query, fetchOptions]);

  const handleCreate = async () => {
    if (!selected) return;
    try {
      await PrivateAdminPanelInstance.post('create-new-shortcut', {
        entity_id: selected.entity_id,
        entity_type: ENTITY_TYPE,
        item_id: selected.item_id,
        item_name: selected.item_name,
        item_type: selected.item_type,
      });
      await onCreated();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to create');
    }
  };

  return (
    <div className="add-new-ip-panel">
      <div className="modal-header">
        <div className="modal-header-title">
          <h2 className="modal-title">Add Shortcut</h2>
        </div>
        <X onClick={onClose} className="close-icon" />
      </div>
      <div className="divider" />
      <div className="msb-modal-body">
        <div className="autocomplete">
          <Input
            placeholder="Search sports or leagues"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query ? (
            <div className="results">
              {loading ? (
                <div className="center"><Spin size="small" /></div>
              ) : (
                options.map((opt) => {
                  const showCountryTag = opt.item_type === 'LEAGUE' && (opt.country_tag || opt.country_name);
                  return (
                    <div
                      key={opt.entity_id}
                      className={`result-item ${selected?.entity_id === opt.entity_id ? 'selected' : ''}`}
                      onClick={() => setSelected(opt)}
                    >
                      <div className="result-main">
                        <span className="result-name">{opt.item_name}</span>
                        {showCountryTag ? (
                          <Tag
                            size="small"
                            color="purple"
                            className="result-tag result-country-tag"
                          >
                            {opt.country_tag || opt.country_name}
                          </Tag>
                        ) : null}
                        <Tag
                          size="small"
                          color={opt.item_type === 'LEAGUE' ? 'blue' : 'green'}
                          className="result-tag result-type-tag"
                        >
                          {opt.item_type}
                        </Tag>
                      </div>
                      {opt.sport_name ? (
                        <span className="result-sub">{opt.sport_name}</span>
                      ) : null}
                    </div>
                  );
                })
              )}
              {!loading && options.length === 0 && query && (
                <div className="empty-mini">No results</div>
              )}
            </div>
          ) : null}
        </div>
        {error ? <div className="error-msg">{error}</div> : null}
      </div>
      <div className="modal-footer">
        <button className="cancel-button" onClick={onClose}>Close</button>
        <button className={`submit-button ${!selected ? 'disabled' : ''}`} disabled={!selected} onClick={handleCreate}>Add Shortcut</button>
      </div>
    </div>
  );
};

const DeleteShortcutModal = ({ onClose, onConfirm, busy = false }) => {
  return (
    <div className="add-new-ip-panel">
      <div className="modal-header">
        <div className="modal-header-title">
          <h2 className="modal-title">Delete Shortcut</h2>
        </div>
        <X onClick={onClose} className="close-icon" />
      </div>
      <div className="divider" />
      <div className="msb-modal-body">
        <p>Are you sure you want to delete this shortcut? This action cannot be undone.</p>
      </div>
      <div className="modal-footer">
        <button className="cancel-button" onClick={onClose} disabled={busy}>Close</button>
        <button className={`submit-button delete-button ${busy ? 'disabled' : ''}`} onClick={onConfirm} disabled={busy}>{busy ? 'Deleting...' : 'Delete'}</button>
      </div>
    </div>
  );
};

const RenameShortcutModal = ({ target, onClose, onConfirm, saving = false }) => {
  const [name, setName] = useState(target?.display_name || '');
  useEffect(() => {
    setName(target?.display_name || '');
  }, [target?.entity_id, target?.display_name]);
  return (
    <div className="add-new-ip-panel">
      <div className="modal-header">
        <div className="modal-header-title">
          <h2 className="modal-title">Rename Shortcut</h2>
        </div>
        <X onClick={onClose} className="close-icon" />
      </div>
      <div className="divider" />
      <div className="msb-modal-body">
        <label htmlFor="shortcutName">Display Name</label>
        <Input id="shortcutName" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="modal-footer">
        <button className="cancel-button" onClick={onClose} disabled={saving}>Close</button>
        <button className={`submit-button ${!name?.trim() || saving ? 'disabled' : ''}`} disabled={!name?.trim() || saving} onClick={() => onConfirm(name?.trim())}>{saving ? 'Saving...' : 'Save'}</button>
      </div>
    </div>
  );
};

export default Shortcuts;
